import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { Language } from "../../App";
import {
  LuStethoscope,
  LuFileText,
  LuVideo,
  LuActivity,
  LuGraduationCap,
  LuCalendar,
  LuFilter,
} from "react-icons/lu";
import { MdOutlineDynamicFeed } from "react-icons/md";

/* ── matching per-type accent config from PostSideFilters ── */
const TYPE_CONFIG = {
  doctors:  { color: "#00BADA", light: "#E8FBFF", border: "#BCEEFA", shadow: "rgba(0, 186, 218, 0.22)" },
  posts:    { color: "#3B82F6", light: "#EFF6FF", border: "#BFDBFE", shadow: "rgba(59, 130, 246, 0.22)" },
  webinars: { color: "#EF4444", light: "#FEF2F2", border: "#FECACA", shadow: "rgba(239, 68, 68, 0.22)" },
  studies:  { color: "#10B981", light: "#ECFDF5", border: "#A7F3D0", shadow: "rgba(16, 185, 129, 0.22)" },
  courses:  { color: "#8B5CF6", light: "#F5F3FF", border: "#DDD6FE", shadow: "rgba(139, 92, 246, 0.22)" },
  events:   { color: "#F59E0B", light: "#FFFBEB", border: "#FDE68A", shadow: "rgba(245, 158, 11, 0.22)" },
};

const getConfig = (id) => TYPE_CONFIG[id] || TYPE_CONFIG.doctors;

export default function SearchSidebarFilter({ onSelectCategory, activeCategory, counts, loading }) {
  const { lang } = useContext(Language);

  const list = [
    { id: "doctors", anchor: "doctors", title: lang?.doctors || "Doctors", icon: LuStethoscope },
    { id: "posts", anchor: "posts", title: lang?.posts || "Posts", icon: LuFileText },
    { id: "webinars", anchor: "webinars", title: lang?.webinars || "Webinars", icon: LuVideo },
    { id: "studies", anchor: "clinical-studies", title: lang?.clinical_studies || "Clinical Studies", icon: LuActivity },
    { id: "courses", anchor: "courses", title: lang?.courses || "Courses", icon: LuGraduationCap },
    { id: "events", anchor: "events", title: lang?.events || "Events", icon: LuCalendar },
  ];

  const [active, setActive] = useState(activeCategory || list[0].id);
  const itemRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  const totalCount = useMemo(() => {
    if (!counts) return undefined;
    return Object.values(counts).reduce((acc, curr) => acc + (Number(curr) || 0), 0);
  }, [counts]);

  useEffect(() => {
    if (activeCategory) {
      setActive(activeCategory);
    }
  }, [activeCategory]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const activeItem = itemRefs.current[active];
      if (activeItem) {
        setIndicatorStyle({
          top: activeItem.offsetTop,
          height: activeItem.offsetHeight,
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [active]);

  const handleItemClick = (item) => {
    setActive(item.id);
    if (onSelectCategory) {
      onSelectCategory(item.id);
    }
    const targetElement = document.getElementById(item.anchor);
    if (targetElement) {
      const navOffset = 140;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } else {
      window.location.hash = `#${item.anchor}`;
    }
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* ── Ambient glow ── */}
      <div
        style={{
          position: "absolute",
          top: "-20px",
          right: "-20px",
          width: "90px",
          height: "90px",
          background: "#00BADA",
          opacity: 0.08,
          filter: "blur(35px)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div className="psf-card">
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between px-4 py-3"
          style={{
            borderBottom: "1px solid #F1F5F9",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 28,
                height: 28,
                background: "#E8FBFF",
                color: "#00BADA",
                border: "1px solid #BCEEFA",
              }}
            >
              <LuFilter size={15} />
            </div>
            <h4
              className="fs-15 roboto-bold mb-0"
              style={{ color: "#0F1A2B", lineHeight: 1.2 }}
            >
              {lang?.see_results_for || "See Results For"}
            </h4>
          </div>

          {/* Total Count Badge */}
          {totalCount !== undefined && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                minWidth: "22px",
                height: "22px",
                padding: "0 6px",
                borderRadius: "9999px",
                fontSize: "11px",
                fontWeight: 700,
                lineHeight: 1,
                color: "#0098B3",
                background: "#E8FBFF",
                border: "1px solid #BCEEFA",
                boxShadow: "0 2px 6px rgba(0, 186, 218, 0.12)",
                marginLeft: "auto",
                flexShrink: 0,
                boxSizing: "border-box",
              }}
            >
              <span style={{ lineHeight: 1, display: "inline-block" }}>
                {loading ? "-" : totalCount}
              </span>
            </span>
          )}
        </div>

        {/* Filter list */}
        <ul className="list-unstyled mb-0" style={{ position: "relative" }}>
          {/* Animated left indicator bar */}
          <div
            className="psf-indicator"
            style={{
              height: `${indicatorStyle.height}px`,
              transform: `translateY(${indicatorStyle.top}px)`,
            }}
          />

          {list.map((item) => {
            const isActive = active === item.id;
            const cfg = getConfig(item.id);
            const IconEl = item.icon;
            const count = counts ? counts[item.id] : undefined;

            return (
              <li key={item.id}>
                <button
                  ref={(el) => (itemRefs.current[item.id] = el)}
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(e) => e.key === "Enter" && handleItemClick(item)}
                  className="psf-item"
                  style={{
                    background: isActive ? cfg.light : "transparent",
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Filter by ${item.title}`}
                  aria-current={isActive ? "true" : undefined}
                >
                  {/* Icon badge */}
                  <div
                    className="psf-icon-wrap"
                    style={{
                      background: isActive ? cfg.light : "#F1F5F9",
                      border: `1px solid ${isActive ? cfg.border : "transparent"}`,
                    }}
                  >
                    <IconEl
                      size={17}
                      style={{ color: isActive ? cfg.color : "#64748B" }}
                    />
                  </div>

                  {/* Label */}
                  <span
                    className="psf-label"
                    style={{
                      color: isActive ? cfg.color : "#475569",
                      fontWeight: isActive ? 700 : 500,
                    }}
                  >
                    {item.title}
                  </span>

                  {/* Count badge or active dot */}
                  {count !== undefined ? (
                    <span
                      style={{
                        marginLeft: "auto",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        minWidth: "22px",
                        height: "22px",
                        padding: "0 6px",
                        borderRadius: "9999px",
                        fontSize: "11px",
                        fontWeight: isActive ? 700 : 600,
                        lineHeight: 1,
                        color: isActive ? "#FFFFFF" : "#64748B",
                        backgroundColor: isActive ? cfg.color : "#F1F5F9",
                        border: `1px solid ${isActive ? cfg.color : "#E2E8F0"}`,
                        boxShadow: isActive ? `0 2px 8px ${cfg.shadow}` : "none",
                        transition: "all 0.18s ease",
                        flexShrink: 0,
                        boxSizing: "border-box",
                      }}
                    >
                      <span style={{ lineHeight: 1, display: "inline-block" }}>
                        {loading ? "-" : count}
                      </span>
                    </span>
                  ) : (
                    isActive && (
                      <div
                        style={{
                          marginLeft: "auto",
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: cfg.color,
                          flexShrink: 0,
                          boxShadow: `0 0 6px ${cfg.color}88`,
                        }}
                      />
                    )
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
